import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import api, { API_URL } from '../utils/api';
import Layout from '../components/Layout';
import { StatusBadge, CategoryBadge, HashtagChip, VisibilityBadge } from '../components/Badges';
import ReplyList from '../components/ReplyItem';
import AssignButton from '../components/AssignButton';
import { toast } from 'sonner';
import { ArrowLeft, Paperclip, Send, Languages, Loader2, MessageSquare, CornerDownRight, AlertTriangle, Clock, CheckCircle, Globe, Lock, ArrowUpRight, ChevronUp } from 'lucide-react';

export default function TicketDetailPage() {
  var params = useParams();
  var ticketId = params.ticketId;
  var authCtx = useAuth();
  var user = authCtx.user;
  var langCtx = useLang();
  var lang = langCtx.lang;
  var navigate = useNavigate();
  var [ticket, setTicket] = useState(null);
  var [replies, setReplies] = useState([]);
  var [loading, setLoading] = useState(true);
  var [replyText, setReplyText] = useState('');
  var [replyTranslated, setReplyTranslated] = useState('');
  var [translating, setTranslating] = useState(false);
  var [sending, setSending] = useState(false);
  var [replyToId, setReplyToId] = useState(null);
  var [newStatus, setNewStatus] = useState('');
  var [replyFiles, setReplyFiles] = useState([]);
  var [validating, setValidating] = useState(false);
  var [showEscalate, setShowEscalate] = useState(false);
  var [escalationTargets, setEscalationTargets] = useState([]);
  var [escalating, setEscalating] = useState(false);
  var [postAs, setPostAs] = useState('self'); // 'self' | 'support' — uniquement effectif si user.role in [direction, support]
  var [retranslating, setRetranslating] = useState(false);
  var replyTimer = useRef(null);

  var fetchData = useCallback(function() {
    return Promise.all([
      api.get('/tickets/' + ticketId),
      api.get('/tickets/' + ticketId + '/replies')
    ]).then(function(results) {
      setTicket(results[0].data);
      setReplies(results[1].data);
      setNewStatus(results[0].data.status);
    }).catch(function(err) {
      console.error(err);
    }).finally(function() {
      setLoading(false);
    });
  }, [ticketId]);

  useEffect(function() { fetchData(); }, [fetchData]);

  useEffect(function() {
    if (!replyText.trim()) { setReplyTranslated(''); return; }
    clearTimeout(replyTimer.current);
    replyTimer.current = setTimeout(function() {
      setTranslating(true);
      // Traduire dans le sens : langue active → langue opposée
      api.post('/translate', { text: replyText, source_lang: lang, target_lang: lang === 'fr' ? 'en' : 'fr' })
        .then(function(res) { setReplyTranslated(res.data.translated_text); })
        .catch(function() {})
        .finally(function() { setTranslating(false); });
    }, 800);
    return function() { clearTimeout(replyTimer.current); };
  }, [replyText, lang]);

  function handleSendReply() {
    if (!replyText.trim()) return;
    setSending(true);
    // replyText = saisie dans la langue active ; replyTranslated = autre langue.
    // Mapper vers content_fr / content_en selon la langue active.
    var contentFr = lang === 'fr' ? replyText : replyTranslated;
    var contentEn = lang === 'fr' ? replyTranslated : replyText;
    api.post('/tickets/' + ticketId + '/replies', {
      content_fr: contentFr,
      content_en: contentEn,
      parent_reply_id: replyToId,
      posted_as: postAs === 'support' ? 'support' : null
    }).then(function(res) {
      var newReplyId = res.data && res.data._id;
      // Upload reply attachments sequentially if any
      var uploads = Promise.resolve();
      if (newReplyId && replyFiles.length > 0) {
        uploads = replyFiles.reduce(function(prev, f) {
          return prev.then(function() {
            var fd = new FormData();
            fd.append('file', f);
            return api.post('/replies/' + newReplyId + '/attachments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          });
        }, Promise.resolve());
      }
      return uploads.then(function() {
        setReplyText('');
        setReplyTranslated('');
        setReplyToId(null);
        setReplyFiles([]);
        setPostAs('self');  // reset signature toggle pour éviter de signer Support par accident sur le prochain reply
        fetchData();
      });
    }).catch(function(err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Reply failed');
    }).finally(function() {
      setSending(false);
    });
  }

  function handleStatusChange(status) {
    var wasClosed = ticket && ticket.status === 'closed';
    api.put('/tickets/' + ticketId, { status: status })
      .then(function() {
        setNewStatus(status);
        if (wasClosed && status !== 'closed') {
          toast.success(t('reopen_toast', lang));
        }
        fetchData();
      })
      .catch(function(err) {
        toast.error(err.response?.data?.detail || 'Error');
      });
  }

  function handleFileUpload(e) {
    var file = e.target.files[0];
    if (!file) return;
    var formData = new FormData();
    formData.append('file', file);
    api.post('/tickets/' + ticketId + '/attachments', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(function() { fetchData(); })
      .catch(function(err) { alert(err.response?.data?.detail || 'Upload failed'); });
    e.target.value = '';
  }

  function handleReplyFileAdd(e) {
    var file = e.target.files[0];
    if (!file) return;
    if (replyFiles.length >= 5) { alert('Max 5 files'); return; }
    setReplyFiles(function(prev) { return prev.concat([file]); });
    e.target.value = '';
  }

  function handleValidatePublic() {
    setValidating(true);
    api.put('/tickets/' + ticketId + '/validate-public')
      .then(function() { fetchData(); })
      .catch(function(err) { alert(err.response?.data?.detail || 'Error'); })
      .finally(function() { setValidating(false); });
  }

  function handleRejectPublic() {
    setValidating(true);
    api.put('/tickets/' + ticketId + '/reject-public')
      .then(function() { fetchData(); })
      .catch(function(err) { alert(err.response?.data?.detail || 'Error'); })
      .finally(function() { setValidating(false); });
  }

  function loadEscalationTargets() {
    api.get('/tickets/' + ticketId + '/escalation-targets')
      .then(function(res) { setEscalationTargets(res.data); setShowEscalate(true); })
      .catch(function() { setEscalationTargets([]); });
  }

  function handleEscalate(targetId) {
    setEscalating(true);
    api.put('/tickets/' + ticketId + '/escalate', { target_user_id: targetId })
      .then(function() { setShowEscalate(false); fetchData(); })
      .catch(function(err) { alert(err.response?.data?.detail || 'Error'); })
      .finally(function() { setEscalating(false); });
  }

  if (loading) return React.createElement(Layout, null, React.createElement('p', { className: "text-center py-12", style: { color: '#6B7280' } }, t('loading', lang)));
  if (!ticket) return React.createElement(Layout, null, React.createElement('p', { className: "text-center py-12", style: { color: '#6B7280' } }, 'Ticket not found'));

  var canManage = user && ['consultant', 'dr', 'dn', 'direction', 'support'].includes(user.role);
  var isSupport = user && user.role === 'support';
  var isPendingPublic = ticket.visibility === 'pending_public';

  // === Ticket locking (status-based reply rules) — miroir du helper backend user_can_reply_to_ticket() ===
  var isCreator = user && ticket.created_by === user._id;
  var canReply = true;
  var lockReason = null;  // 'closed' | 'resolved' | null
  if (ticket.status === 'closed') { canReply = false; lockReason = 'closed'; }
  else if (ticket.status === 'resolved') {
    if (!isCreator) { canReply = false; lockReason = 'resolved'; }
  }
  // Status dropdown restrictions: seul HIGH_ROLES peut transitionner depuis 'closed'
  var isHighRole = user && (user.role === 'support' || user.role === 'direction');
  var statusDropdownLocked = ticket.status === 'closed' && !isHighRole;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto animate-fade-in">
        <button data-testid="back-to-list" onClick={function() { navigate(-1); }} className="flex items-center gap-2 mb-6 text-sm" style={{ color: '#9CA3AF' }}>
          <ArrowLeft size={16} /> {t('my_tickets', lang)}
        </button>

        {/* SUPPORT VALIDATION BANNER */}
        {isSupport && isPendingPublic ? (
          <div data-testid="public-validation-banner" className="mb-4 p-4 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" style={{ background: 'rgba(247,127,0,0.1)', border: '2px solid rgba(247,127,0,0.3)' }}>
            <div className="flex items-center gap-3 min-w-0">
              <Globe size={22} style={{ color: '#F77F00', flexShrink: 0 }} />
              <p className="text-sm font-medium text-white">{t('validate_public_banner', lang)}</p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap">
              <button data-testid="validate-public-btn" onClick={handleValidatePublic} disabled={validating}
                className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2" style={{ background: '#22C55E', color: '#fff', minHeight: '44px' }}>
                <CheckCircle size={16} /> {t('validate_as_public', lang)}
              </button>
              <button data-testid="reject-public-btn" onClick={handleRejectPublic} disabled={validating}
                className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2" style={{ background: 'rgba(107,114,128,0.3)', color: '#E8E8E8', minHeight: '44px' }}>
                <Lock size={16} /> {t('keep_private', lang)}
              </button>
            </div>
          </div>
        ) : null}

        <div className="apollo-card p-4 sm:p-6 mb-4">
          <div className="mb-4">
            <h1 data-testid="ticket-title" className="text-lg sm:text-xl font-bold text-white mb-2">{lang === 'en' ? (ticket.title_en || ticket.title_fr) : (ticket.title_fr || ticket.title_en)}</h1>
            {ticket.title_en && lang === 'fr' && ticket.title_en !== ticket.title_fr ? <p className="text-sm italic mb-2" style={{ color: '#9CA3AF' }}>EN: {ticket.title_en}</p> : null}
            {ticket.title_fr && lang === 'en' && ticket.title_fr !== ticket.title_en ? <p className="text-sm italic mb-2" style={{ color: '#9CA3AF' }}>FR: {ticket.title_fr}</p> : null}
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <CategoryBadge category={lang === 'en' ? t(ticket.category, lang) : ticket.category} />
              <StatusBadge status={ticket.status} />
              <VisibilityBadge visibility={ticket.visibility || 'private'} lang={lang} />
              {ticket.hashtags ? ticket.hashtags.map(function(tag) { return <HashtagChip key={tag} tag={tag} onClick={function(h) { navigate('/tickets?hashtag=' + h); }} />; }) : null}
            </div>
            {canManage ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium" style={{ color: '#9CA3AF' }}>{t('status', lang)}:</span>
                <select
                  data-testid="status-selector"
                  value={newStatus}
                  onChange={function(e) { handleStatusChange(e.target.value); }}
                  disabled={statusDropdownLocked}
                  className="apollo-input w-full sm:w-auto"
                  style={{ minHeight: '44px', opacity: statusDropdownLocked ? 0.6 : 1, cursor: statusDropdownLocked ? 'not-allowed' : 'pointer' }}
                  title={statusDropdownLocked ? t('reopen_denied', lang) : ''}
                >
                  {statusDropdownLocked ? (
                    <option value="closed">{t('closed', lang)}</option>
                  ) : (
                    <>
                      <option value="open">{t('open', lang)}</option>
                      <option value="in_progress">{t('in_progress', lang)}</option>
                      <option value="resolved">{t('resolved', lang)}</option>
                      <option value="closed">{t('closed', lang)}</option>
                    </>
                  )}
                </select>
              </div>
            ) : null}
          </div>

          {/* Escalation Panel */}
          {user && user.role !== 'support' && user.role !== 'direction' && ticket.status !== 'closed' && ticket.status !== 'resolved' ? (
            <div className="mb-4">
              {!showEscalate ? (
                <button data-testid="escalate-button" onClick={loadEscalationTargets}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                  style={{ background: 'rgba(236,72,153,0.1)', color: '#EC4899', border: '1px solid rgba(236,72,153,0.2)' }}>
                  <ArrowUpRight size={16} /> {lang === 'fr' ? 'Escalader ce ticket' : 'Escalate this ticket'}
                </button>
              ) : (
                <div data-testid="escalation-panel" className="p-4 rounded-xl animate-fade-in" style={{ background: 'rgba(236,72,153,0.06)', border: '1px solid rgba(236,72,153,0.2)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <ChevronUp size={16} style={{ color: '#EC4899' }} />
                      <span className="text-sm font-semibold" style={{ color: '#EC4899' }}>{lang === 'fr' ? 'Escalader vers' : 'Escalate to'}</span>
                    </div>
                    <button onClick={function() { setShowEscalate(false); }} style={{ color: '#6B7280' }}><Lock size={14} /></button>
                  </div>
                  {escalationTargets.length === 0 ? (
                    <p className="text-xs" style={{ color: '#9CA3AF' }}>{lang === 'fr' ? 'Aucune cible d\'escalade disponible' : 'No escalation targets available'}</p>
                  ) : (
                    <div className="space-y-2">
                      {escalationTargets.map(function(target) {
                        return (
                          <button key={target._id} data-testid={'escalate-to-' + target._id}
                            onClick={function() { handleEscalate(target._id); }}
                            disabled={escalating}
                            className="w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left"
                            style={{ background: '#1A1A2E', border: '1px solid rgba(255,255,255,0.08)' }}>
                            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(236,72,153,0.15)' }}>
                              <span style={{ color: '#EC4899', fontWeight: 600, fontSize: 12 }}>{target.name ? target.name.charAt(0) : '?'}</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-white">{target.name}</p>
                              <p className="text-xs" style={{ color: '#6B7280' }}>{t(target.role, lang)} {target.email ? '- ' + target.email : ''}</p>
                            </div>
                            <ArrowUpRight size={16} style={{ color: '#EC4899' }} />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}

          {/* Escalation History */}
          {ticket.escalation_history && ticket.escalation_history.length > 0 ? (
            <div className="mb-4 p-3 rounded-lg" style={{ background: 'rgba(236,72,153,0.04)', border: '1px solid rgba(236,72,153,0.1)' }}>
              <p className="text-xs font-semibold mb-2" style={{ color: '#EC4899' }}>{lang === 'fr' ? 'Historique d\'escalade' : 'Escalation History'}</p>
              {ticket.escalation_history.map(function(entry, idx) {
                return (
                  <div key={idx} className="flex items-center gap-2 text-xs py-1" style={{ color: '#9CA3AF' }}>
                    <ArrowUpRight size={10} style={{ color: '#EC4899' }} />
                    <span>{entry.from_user_name} ({entry.from_role})</span>
                    <span style={{ color: '#6B7280' }}>→</span>
                    <span className="text-white">{entry.to_user_name}</span>
                    <span style={{ color: '#6B7280' }}>{new Date(entry.timestamp).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US')}</span>
                  </div>
                );
              })}
            </div>
          ) : null}

          {/* SLA Status Banner */}
          {ticket.sla ? (
            <div data-testid="sla-status" className="mb-4 p-3 rounded-lg flex items-center gap-3" style={{
              background: ticket.sla.sla_breached && !ticket.sla.sla_responded ? 'rgba(239,68,68,0.1)' :
                          ticket.sla.sla_responded ? 'rgba(34,197,94,0.08)' :
                          ticket.sla.sla_hours_remaining < 4 ? 'rgba(251,191,36,0.08)' : 'rgba(247,127,0,0.05)',
              border: '1px solid ' + (ticket.sla.sla_breached && !ticket.sla.sla_responded ? 'rgba(239,68,68,0.2)' :
                          ticket.sla.sla_responded ? 'rgba(34,197,94,0.15)' :
                          ticket.sla.sla_hours_remaining < 4 ? 'rgba(251,191,36,0.15)' : 'rgba(247,127,0,0.1)')
            }}>
              {ticket.sla.sla_breached && !ticket.sla.sla_responded ? (
                <AlertTriangle size={18} style={{ color: '#EF4444', flexShrink: 0 }} />
              ) : ticket.sla.sla_responded ? (
                <CheckCircle size={18} style={{ color: '#22C55E', flexShrink: 0 }} />
              ) : (
                <Clock size={18} style={{ color: ticket.sla.sla_hours_remaining < 4 ? '#FBBF24' : '#F77F00', flexShrink: 0 }} />
              )}
              <div>
                <p className="text-sm font-medium" style={{
                  color: ticket.sla.sla_breached && !ticket.sla.sla_responded ? '#EF4444' :
                         ticket.sla.sla_responded ? '#22C55E' : '#E8E8E8'
                }}>
                  {ticket.sla.sla_breached && !ticket.sla.sla_responded
                    ? (lang === 'fr' ? 'SLA dépassé — Aucune réponse en 24h !' : 'SLA Breached — No response in 24h!')
                    : ticket.sla.sla_responded
                    ? (lang === 'fr' ? 'Répondu en ' + ticket.sla.sla_response_hours + 'h' + (ticket.sla.sla_breached ? ' (SLA dépassé)' : ' (SLA respecté)') : 'Responded in ' + ticket.sla.sla_response_hours + 'h' + (ticket.sla.sla_breached ? ' (SLA breached)' : ' (SLA met)'))
                    : (lang === 'fr' ? 'SLA : ' + ticket.sla.sla_hours_remaining + 'h restantes pour répondre' : 'SLA: ' + ticket.sla.sla_hours_remaining + 'h remaining to respond')
                  }
                </p>
                <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
                  {lang === 'fr' ? 'Délai SLA : 24 heures pour la première réponse' : 'SLA Target: 24 hours for first response'}
                </p>
              </div>
            </div>
          ) : null}

          <div className="flex items-center gap-2 sm:gap-4 text-xs mb-4 flex-wrap" style={{ color: '#6B7280' }}>
            <span>{t('created_by', lang)} <strong className="text-white">{ticket.created_by_name}</strong></span>
            <span>{t('created_at', lang)} {new Date(ticket.created_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}</span>
            {ticket.assigned_to_name ? <span>{t('assigned_to', lang)} <strong className="text-white">{ticket.assigned_to_name}</strong></span> : (
              <>
                <span data-testid="ticket-detail-unassigned" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>
                  <AlertTriangle size={11} /> {lang === 'fr' ? 'À assigner' : 'To assign'}
                </span>
                {(user?.role !== 'member' || ticket.created_by === user?._id) && (
                  <AssignButton
                    ticketId={ticket._id}
                    size="sm"
                    onAssigned={(u) => {
                      toast.success(lang === 'fr' ? ('Ticket assigné à ' + (u?.name || '')) : ('Ticket assigned to ' + (u?.name || '')));
                      fetchData();
                    }}
                  />
                )}
              </>
            )}
          </div>

          {/* Description */}
          <div className="p-4 rounded-lg mb-4" style={{ background: '#0F0F1A' }}>
            <p className="text-sm text-white whitespace-pre-wrap">{lang === 'en' ? (ticket.description_en || ticket.description_fr) : (ticket.description_fr || ticket.description_en)}</p>
            {ticket.description_en && lang === 'fr' && ticket.description_en !== ticket.description_fr ? (
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-1 mb-1"><Languages size={12} style={{ color: '#F77F00' }} /><span className="text-xs" style={{ color: '#F77F00' }}>EN</span></div>
                <p className="text-sm whitespace-pre-wrap" style={{ color: '#9CA3AF' }}>{ticket.description_en}</p>
              </div>
            ) : null}
            {ticket.description_fr && lang === 'en' && ticket.description_fr !== ticket.description_en ? (
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-1 mb-1"><Languages size={12} style={{ color: '#F77F00' }} /><span className="text-xs" style={{ color: '#F77F00' }}>FR</span></div>
                <p className="text-sm whitespace-pre-wrap" style={{ color: '#9CA3AF' }}>{ticket.description_fr}</p>
              </div>
            ) : null}
            {/* Bouton "Re-traduire" pour Direction/Support : utile pour les vieux tickets buggés */}
            {user && (user.role === 'direction' || user.role === 'support') ? (
              <button
                type="button"
                data-testid="retranslate-ticket-button"
                onClick={async function() {
                  if (retranslating) return;
                  setRetranslating(true);
                  try {
                    await api.post('/tickets/' + ticketId + '/retranslate');
                    await fetchData();
                  } catch (e) {} finally { setRetranslating(false); }
                }}
                disabled={retranslating}
                className="mt-3 flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md transition-all"
                style={{ background: 'rgba(247,127,0,0.08)', color: '#F77F00', border: '1px solid rgba(247,127,0,0.2)', opacity: retranslating ? 0.5 : 1 }}
              >
                <Languages size={12} />
                {retranslating ? (lang === 'fr' ? 'Re-traduction…' : 'Re-translating…') : (lang === 'fr' ? 'Re-traduire' : 'Re-translate')}
              </button>
            ) : null}
          </div>

          {/* Attachments */}
          {ticket.attachments && ticket.attachments.length > 0 ? (
            <div className="mb-4">
              <p className="text-xs font-medium mb-2" style={{ color: '#9CA3AF' }}>{t('attachments', lang)} ({ticket.attachments.length})</p>
              <div className="flex gap-2 flex-wrap">
                {ticket.attachments.map(function(att) {
                  var tok = encodeURIComponent(localStorage.getItem('apollo_token') || '');
                  return (
                    <a key={att.id} href={API_URL + '/api/attachments/' + ticketId + '/' + att.id + '?token=' + tok} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs max-w-full"
                      style={{ background: 'rgba(247,127,0,0.08)', color: '#F77F00', border: '1px solid rgba(247,127,0,0.15)' }}>
                      <Paperclip size={12} className="flex-shrink-0" /> <span className="truncate">{att.filename}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Upload button — masqué si ticket verrouillé (closed/resolved hors créateur) */}
          {canReply ? (
            <label className="inline-flex items-center gap-2 cursor-pointer text-xs px-3 py-2 rounded-lg transition-colors" style={{ color: '#F77F00', background: 'rgba(247,127,0,0.08)', border: '1px solid rgba(247,127,0,0.15)' }}>
              <Paperclip size={14} /> {lang === 'fr' ? 'Ajouter une pièce jointe' : 'Add attachment'}
              <input data-testid="file-upload" type="file" className="hidden" onChange={handleFileUpload} accept=".jpg,.jpeg,.png,.pdf,.docx,.xlsx" />
            </label>
          ) : null}
        </div>

        {/* Replies Section */}
        <div className="mb-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-white mb-4">
            <MessageSquare size={20} style={{ color: '#F77F00' }} />
            {t('reply', lang)} ({replies.length})
          </h2>
          {replies.length > 0 ? <ReplyList replies={replies} lang={lang} onReplyTo={setReplyToId} canReply={canReply} /> : null}
        </div>

        {/* Reply Input — conditionnel selon le lock du ticket */}
        {!canReply ? (
          <div
            data-testid={'lock-banner-' + lockReason}
            className="apollo-card p-4 flex items-start gap-3"
            style={{
              background: lockReason === 'closed' ? 'rgba(239,68,68,0.08)' : 'rgba(247,127,0,0.08)',
              border: '1px solid ' + (lockReason === 'closed' ? 'rgba(239,68,68,0.25)' : 'rgba(247,127,0,0.25)')
            }}
          >
            <Lock size={18} style={{ color: lockReason === 'closed' ? '#EF4444' : '#F77F00', flexShrink: 0, marginTop: 2 }} />
            <p className="text-sm" style={{ color: lockReason === 'closed' ? '#FCA5A5' : '#FED7AA' }}>
              {lockReason === 'closed' ? t('lock_closed_banner', lang) : t('lock_resolved_banner', lang)}
            </p>
          </div>
        ) : (
          <div className="apollo-card p-4">
            {isCreator && ticket.status === 'resolved' ? (
              <div data-testid="creator-reopen-hint" className="mb-3 p-2 rounded-md flex items-center gap-2" style={{ background: 'rgba(247,127,0,0.06)', border: '1px solid rgba(247,127,0,0.15)' }}>
                <span className="text-xs" style={{ color: '#F77F00' }}>{t('lock_creator_hint', lang)}</span>
              </div>
            ) : null}
            {replyToId ? (
            <div className="flex items-center gap-2 mb-2 text-xs" style={{ color: '#F77F00' }}>
              <CornerDownRight size={12} /> {lang === 'fr' ? 'Réponse au thread' : 'Replying to thread'}
              <button onClick={function() { setReplyToId(null); }} className="ml-2" style={{ color: '#6B7280' }}>x</button>
            </div>
          ) : null}
          <textarea
            data-testid="reply-input"
            value={replyText}
            onChange={function(e) { setReplyText(e.target.value); }}
            className="apollo-input min-h-[80px] resize-y mb-2"
            placeholder={t('write_reply', lang)}
          />
          {/* Signature toggle : Direction/Support peuvent signer Support APOLLO sans changer de compte */}
          {(user && (user.role === 'direction' || user.role === 'support')) ? (
            <div data-testid="post-as-toggle" className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-xs" style={{ color: '#9CA3AF' }}>{t('post_as_label', lang)}</span>
              <button
                type="button"
                data-testid="post-as-self"
                onClick={function() { setPostAs('self'); }}
                className="text-xs px-3 py-1.5 rounded-full transition-all"
                style={{
                  background: postAs === 'self' ? '#F77F00' : 'rgba(255,255,255,0.04)',
                  color: postAs === 'self' ? '#fff' : '#9CA3AF',
                  border: '1px solid ' + (postAs === 'self' ? '#F77F00' : 'rgba(255,255,255,0.08)')
                }}
              >{user.name}</button>
              <button
                type="button"
                data-testid="post-as-support"
                onClick={function() { setPostAs('support'); }}
                className="text-xs px-3 py-1.5 rounded-full transition-all"
                style={{
                  background: postAs === 'support' ? '#F77F00' : 'rgba(255,255,255,0.04)',
                  color: postAs === 'support' ? '#fff' : '#9CA3AF',
                  border: '1px solid ' + (postAs === 'support' ? '#F77F00' : 'rgba(255,255,255,0.08)')
                }}
              >{t('post_as_support', lang)}</button>
            </div>
          ) : null}
          {replyText ? (
            <div className="p-3 rounded-lg mb-2" style={{ background: 'rgba(247,127,0,0.05)', border: '1px solid rgba(247,127,0,0.1)' }}>
              <div className="flex items-center gap-2 mb-1">
                <Languages size={12} style={{ color: '#F77F00' }} />
                <span className="text-xs" style={{ color: '#F77F00' }}>EN</span>
                {translating ? <Loader2 size={10} className="animate-spin" style={{ color: '#F77F00' }} /> : null}
              </div>
              <p className="text-xs" style={{ color: '#9CA3AF' }}>{replyTranslated || t('translating', lang)}</p>
            </div>
          ) : null}

          {/* Reply attachments */}
          {replyFiles.length > 0 ? (
            <div className="flex gap-2 flex-wrap mb-2">
              {replyFiles.map(function(f, i) {
                return (
                  <span key={i} className="flex items-center gap-1 px-2 py-1 rounded text-xs" style={{ background: 'rgba(247,127,0,0.08)', color: '#F77F00' }}>
                    <Paperclip size={10} /> {f.name}
                    <button onClick={function() { setReplyFiles(function(prev) { return prev.filter(function(_, j) { return j !== i; }); }); }} className="ml-1" style={{ color: '#6B7280' }}>x</button>
                  </span>
                );
              })}
            </div>
          ) : null}

          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <button
              data-testid="send-reply-button"
              onClick={handleSendReply}
              disabled={sending || !replyText.trim()}
              className="apollo-btn-primary flex items-center gap-2 flex-1 sm:flex-initial justify-center"
              style={{ minHeight: '44px' }}
            >
              <Send size={16} /> {t('reply', lang)}
            </button>
            <label data-testid="reply-file-upload-label" className="inline-flex items-center gap-2 cursor-pointer text-sm px-3 py-3 rounded-lg" style={{ color: '#F77F00', background: 'rgba(247,127,0,0.08)', border: '1px solid rgba(247,127,0,0.15)', minHeight: '44px' }}>
              <Paperclip size={16} /> <span className="hidden sm:inline">{t('attachments', lang)}</span>
              <input data-testid="reply-file-upload" type="file" className="hidden" onChange={handleReplyFileAdd} accept=".jpg,.jpeg,.png,.pdf,.docx,.xlsx" />
            </label>
          </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
